import type { NavItem, NavSet } from '@squareup/dex-types-shared-docs';

import {
  LATEST_PUBLISHED_AT,
  assertValidPublishedAt,
  inlineVariantKey,
  isConditionEligible,
  normalizeEntry,
  validateManifestEntry,
  variantPayloadKey,
  variantPublishedAtMs,
} from './mdx-manifest';
import type {
  Manifest,
  ManifestSource,
  PublishingContext,
  QueryLike,
  ResolvedManifestEntry,
  VisibilityCondition,
} from './mdx-manifest';

interface BuildPublishingContextOptions {
  manifest: Manifest;
  query?: QueryLike;
  allowQueryOverrides?: boolean;
  now?: Date;
}

interface FilterNavSetOptions {
  manifest?: Manifest;
}

type PublishableNavItem = NavItem & {
  publishedAt?: string;
  visible?: VisibilityCondition;
};

// Full ISO instant for "now" — the gate compares absolute moments, so we keep
// the time component rather than truncating to a UTC date (which would flip a
// date-only publishedAt live at UTC midnight, i.e. the previous evening in US
// timezones).
function nowInstant(now = new Date()): string {
  return now.toISOString();
}

function getFirstQueryValue(
  query: QueryLike | undefined,
  key: string
): string | undefined {
  const value = query?.[key];
  return Array.isArray(value) ? value[0] : value;
}

function buildPublishingContext(
  options: BuildPublishingContextOptions
): PublishingContext {
  const visibility = Object.fromEntries(
    Object.entries(options.manifest.visibility || {}).map(([name, config]) => [
      name,
      config.default === true,
    ])
  );

  let effectivePublishedAt = nowInstant(options.now);

  if (options.allowQueryOverrides) {
    const publishedAtOverride =
      getFirstQueryValue(options.query, 'publishedAt') ||
      getFirstQueryValue(options.query, 'publish_at');
    if (publishedAtOverride === 'latest') {
      effectivePublishedAt = LATEST_PUBLISHED_AT;
    } else if (publishedAtOverride) {
      assertValidPublishedAt(publishedAtOverride);
      effectivePublishedAt = publishedAtOverride;
    }

    const visibleOverride = getFirstQueryValue(options.query, 'visible');
    if (visibleOverride) {
      for (const token of visibleOverride.split(',')) {
        const trimmed = token.trim();
        if (!trimmed) {
          continue;
        }
        if (trimmed.startsWith('!')) {
          visibility[trimmed.slice(1)] = false;
        } else {
          visibility[trimmed] = true;
        }
      }
    }
  }

  return {
    effectivePublishedAt,
    visibility,
  };
}

function sourceToResolved(
  publicSlug: string,
  source: ManifestSource,
  payloadKey: string,
  context: PublishingContext
): ResolvedManifestEntry {
  const resolvedPayloadKey =
    source.inlineCondition &&
    isConditionEligible(source.inlineCondition, context)
      ? inlineVariantKey(payloadKey, source.inlineCondition)
      : payloadKey;

  return {
    publicSlug,
    source: source.source,
    fileSlug: source.file || publicSlug,
    payloadKey: resolvedPayloadKey,
    markdownKey: resolvedPayloadKey,
    ...(source.inlineCondition
      ? { inlineCondition: source.inlineCondition }
      : {}),
  };
}

function resolveManifestEntry(
  manifest: Manifest,
  publicSlug: string,
  context: PublishingContext
): ResolvedManifestEntry | null {
  const entry = manifest.entries[publicSlug];
  if (!entry) {
    return null;
  }

  validateManifestEntry(publicSlug, entry, {
    visibilityKeys: new Set(Object.keys(manifest.visibility || {})),
  });

  const { defaultSource, variants } = normalizeEntry(publicSlug, entry);
  const eligibleVariants = variants
    .map((variant, index) => ({ variant, index }))
    .filter(({ variant }) => isConditionEligible(variant.when, context))
    .sort(
      (a, b) =>
        variantPublishedAtMs(a.variant) - variantPublishedAtMs(b.variant)
    );

  const selectedVariant = eligibleVariants.at(-1);
  if (selectedVariant) {
    return sourceToResolved(
      publicSlug,
      selectedVariant.variant,
      variantPayloadKey(
        publicSlug,
        selectedVariant.variant,
        selectedVariant.index
      ),
      context
    );
  }

  return sourceToResolved(publicSlug, defaultSource, publicSlug, context);
}

function isNavItemEligible(
  item: PublishableNavItem,
  context: PublishingContext
): boolean {
  return isConditionEligible(
    {
      ...(item.publishedAt ? { publishedAt: item.publishedAt } : {}),
      ...(item.visible !== undefined ? { visible: item.visible } : {}),
    },
    context
  );
}

function docsUrlToSlug(url: string | undefined): string | null {
  if (!url) {
    return null;
  }

  const pathname = url.split('#')[0]?.split('?')[0] || '';
  const slug = pathname.replace(/^\/docs(?=\/|$)\/?/, '');
  return slug || null;
}

function hasNavigableLandingPage(
  item: NavItem,
  context: PublishingContext,
  options: FilterNavSetOptions
): boolean {
  if (item.type !== 'subcategory') {
    return false;
  }

  const slug = docsUrlToSlug(item.url);
  if (!slug) {
    return false;
  }

  if (!options.manifest) {
    return true;
  }

  const resolved = resolveManifestEntry(options.manifest, slug, context);
  return !resolved || resolved.source !== 'none';
}

function filterNavItems(
  items: NavSet['items'],
  context: PublishingContext,
  options: FilterNavSetOptions
): NavSet['items'] {
  const filtered: NavSet['items'] = [];

  for (const item of items) {
    if (item.type === 'divider') {
      filtered.push(item);
      continue;
    }

    if (!isNavItemEligible(item as PublishableNavItem, context)) {
      continue;
    }

    if (item.type === 'subcategory') {
      const childItems = filterNavItems(item.items, context, options);
      if (
        childItems.length === 0 &&
        !hasNavigableLandingPage(item, context, options)
      ) {
        continue;
      }

      filtered.push({
        ...item,
        items: childItems,
      });
      continue;
    }

    filtered.push(item);
  }

  return filtered;
}

function filterNavSet(
  navSet: NavSet,
  context: PublishingContext,
  options: FilterNavSetOptions = {}
): NavSet {
  return {
    ...navSet,
    items: filterNavItems(navSet.items, context, options),
  };
}

export { buildPublishingContext, filterNavSet, resolveManifestEntry };
export type { BuildPublishingContextOptions, FilterNavSetOptions };
